import { useState, useEffect, useRef } from "react";
import VirtualProfile from "./Comment/VirtualProfile";
import { useSession } from "next-auth/react";
import Button from "components/common/buttons/buttons";
import { TYPO } from "styles/Typo";
import { useDebateContext } from "contexts/DebateContext";
import {
  createOpinion,
  createOpinionComment,
  updateOpinion,
  updateOpinionComment,
} from "utils/clientAPIs/debate";
import CustomToast from "components/atoms/CustomToast";
import { CommnetComponentsType } from "types/main";
import { useDebateDetailContext } from "contexts/DebateDetailContext";
import { useMainCommentListContext } from "./MainCommentList";
import { useDebateCleanMode } from "states/debateState";
import * as Sentry from "@sentry/nextjs";
import { useReportComment } from "lib/analytics/react/hooks";

function CommentInput({
  type,
  mode = "input",
  opinionId,
  setAllComments,
  detailMutate,
  editText,
  setMode,
  commentId,
  isNewComment = false,
}: {
  type: CommnetComponentsType;
  mode: "input" | "edit";
  opinionId?: number | null;
  setAllComments?: any;
  detailMutate?: any;
  editText?: string;
  setMode?: (mode: "read" | "edit") => void;
  commentId?: number;
  isNewComment?: boolean;
}) {
  const {
    voteId,
    discussId,
    mutate,
    triggerMutate,
    mutateMapRef,
    setAlarmTrigger,
  } = useDebateContext();
  const { check } = require("korcen");
  const debateDetailContext = useDebateDetailContext();
  const mainCommentListContext = useMainCommentListContext();

  const { data: session } = useSession();
  const [text, setText] = useState("");
  const [errorToast, setErrorToast] = useState<string | boolean>(false);
  const [successToast, setSuccessToast] = useState<string | boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { start, success, fail, blocked } = useReportComment();

  const textLineHeight = 24;

  useEffect(() => {
    if (mode === "edit" && editText) {
      setText(editText);
    }
  }, [mode]);

  // 텍스트가 변경될 때마다 높이 조절
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // 높이를 먼저 초기화
      textarea.style.height = "24px";
      // 스크롤 높이를 기준으로 높이 설정
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = scrollHeight + "px";
    }
  }, [text]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length > 1000) {
      return;
    }
    setText(e.target.value);
  };

  const registerOpinion = async (
    type: CommnetComponentsType,
    email: string,
    opinionId?: number
  ) => {
    if (!voteId || !session?.user?.email) {
      setErrorToast("먼저 투표에 참여해주세요");
      blocked("vote_not_participated");
      return;
    }

    if (text?.trim()?.length === 0) {
      setErrorToast("내용을 입력해주세요");
      blocked("comment_empty");
      return;
    }

    if (!opinionId && type === "comment") {
      return;
    }

    try {
      if (type === "opinion") {
        const res = await createOpinion(email, discussId, voteId, text);

        const sleep = new Promise((resolve) => setTimeout(resolve, 150));
        await sleep;
        mutate();
        setText("");
        if (setAlarmTrigger) setAlarmTrigger("COMMENT");
      } else if (type === "comment") {
        const res = await createOpinionComment(
          email,
          discussId,
          opinionId as number,
          voteId as number,
          text
        );

        if (setAllComments) {
          let myNewCommentObj: any = res?.data;
          const isCensored = myNewCommentObj?.description
            ? check(myNewCommentObj?.description)
            : false;
          myNewCommentObj = {
            ...myNewCommentObj,
            isNewComment: true,
            isCensored,
          };
          setAllComments((prev: any) => {
            const existCommentIds = prev.map((item: any) => item.comment_id);
            if (existCommentIds.includes(myNewCommentObj.comment_id)) {
              return [
                myNewCommentObj,
                ...prev?.filter(
                  (item: any) => item.comment_id !== myNewCommentObj.comment_id
                ),
              ];
            }
            return [myNewCommentObj, ...prev];
          });
        }
        setText("");

        if (detailMutate) {
          await detailMutate();
        }
        mutate();
      } else if (type === "optionDetail") {
        // const res = await updateOption(opinionId, text);
        // mutate();
        // setText("");
      }
      success();
      setSuccessToast(`${type === "comment" ? "댓글" : "의견"}을 등록했어요`);
    } catch (error) {
      console.log("error", error);
      fail();
      Sentry.captureMessage(
        `[Debate] ${type === "comment" ? "댓글" : "의견"} 등록 오류`,
        {
          level: "error",
          tags: {
            errorType: `[Debate] ${
              type === "comment" ? "댓글" : "의견"
            } 등록 오류`,
          },
          extra: { data: error },
        }
      );
    }
  };

  const editHandler = async () => {
    if (text?.trim()?.length === 0) {
      setErrorToast("내용을 입력해주세요");
      return;
    }

    try {
      if (type === "comment") {
        await updateOpinionComment(
          session?.user?.email as string,
          opinionId as number,
          commentId as number,
          text
        );
      } else if (type === "opinion" || type === "optionDetail") {
        await updateOpinion(
          session?.user?.email as string,
          opinionId as number,
          text
        );
      }
      setTimeout(() => {
        mutate();
        if (isNewComment && mainCommentListContext) {
          // 토론 디테일에서 신규 상위 댓글이면 mutate하지 않고 state로만 변경

          const isCensored = text ? check(text) : false;

          mainCommentListContext.setAllComments((prev: any) => {
            const updatedComments = prev.map((item: any) => {
              if (item.comment_id === commentId) {
                return {
                  ...item,
                  description: text,
                  isCensored,
                  updated_at: new Date(),
                };
              }
              return item;
            });
            return updatedComments;
          });
        } else triggerMutate(opinionId as number);

        if (debateDetailContext) debateDetailContext.detailMutate();
        if (setMode) setMode("read");
        setText("");
      }, 200);
    } catch (error) {
      Sentry.captureMessage(
        `[Debate] ${type === "comment" ? "댓글" : "의견"} 수정 오류`,
        {
          level: "error",
          tags: {
            errorType: `[Debate] ${
              type === "comment" ? "댓글" : "의견"
            } 수정 오류`,
          },
          extra: { data: error },
        }
      );
    }
  };

  if (type === "opinion" || type === "optionDetail") {
    return (
      <div className="">
        <div className="w-full relative">
          <div
            className={`flex flex-col items-end gap-[10px] rounded-[14px] ${
              mode === "input" && type === "opinion" ? "p-4" : "px-3 py-2"
            }  bg-gray2 border-[1px] border-gray5`}
          >
            <textarea
              data-test-id={`${type}-${mode}-textarea`}
              ref={textareaRef}
              className={`text-[16px] font-normal !tracking-[-0.16px] !leading-[24px]  text-gray70 w-full  min-h-[26px] debate-comment-textarea bg-gray2`}
              placeholder={
                session?.user?.name
                  ? `${session?.user?.name}님의 의견을 남겨주세요`
                  : "투자자님의 의견을 남겨주세요"
              }
              value={text}
              onChange={handleChange}
              rows={1}
              style={{
                resize: "none",
                maxHeight:
                  type === "optionDetail" && mode === "edit"
                    ? textLineHeight * 8 + textLineHeight * 0.5 + "px"
                    : textLineHeight * 6 + textLineHeight * 0.5 + "px",
                minHeight: textLineHeight + "px",
              }}
              onFocus={(e: any) => {
                start({
                  report_id: discussId,
                  parent_comment_id: opinionId as number,
                });
                if (!voteId || !session?.user?.email) {
                  e?.preventDefault();
                  e.target.blur();
                  setErrorToast("먼저 투표에 참여해주세요");
                  blocked("vote_not_participated");
                }
              }}
            />
            <div className="flex gap-2">
              {mode === "edit" && (
                <Button
                  type="tertiary"
                  size="md"
                  text={"취소"}
                  clickHandler={() => {
                    blocked("comment_cancel");
                    if (setMode) {
                      setMode("read");
                    }
                  }}
                />
              )}
              <Button
                btnClass={`${type}-${mode}-register-button`}
                type="primary"
                size="md"
                text={"등록"}
                clickHandler={() => {
                  if (mode === "input") {
                    registerOpinion(type, session?.user?.email as string);
                    return;
                  }
                  if (mode === "edit") {
                    editHandler();
                  }
                }}
              />
            </div>
          </div>
        </div>
        <CustomToast
          isSuccess={false}
          message={errorToast}
          isVisible={errorToast}
          onClose={() => setErrorToast(false)}
        />
        <CustomToast
          isSuccess={true}
          message={successToast}
          isVisible={successToast}
          onClose={() => setSuccessToast(false)}
          successIconFill={"#34BB7A"}
        />
      </div>
    );
  } else if (type === "comment") {
    return (
      <div className="flex py-2 gap-1 items-stretch">
        <div className="py-2">
          <VirtualProfile
            isMyComment={true}
            nickname={session?.user?.name as string}
          />
        </div>
        <div className="w-full relative">
          <div className="flex flex-col items-end gap-[10px] rounded-[14px] px-3 py-2 bg-gray2 border-[1px] border-gray5">
            <textarea
              data-test-id={`${type}-${mode}-textarea`}
              ref={textareaRef}
              className={`text-[16px] font-normal !tracking-[-0.16px] !leading-[24px]  text-gray70 w-full  min-h-[26px] debate-comment-textarea bg-gray2`}
              placeholder={
                session?.user?.name
                  ? `${session?.user?.name}님의 의견을 남겨주세요`
                  : "투자자님의 의견을 남겨주세요"
              }
              value={text}
              onChange={handleChange}
              rows={1}
              style={{
                resize: "none",
                maxHeight: textLineHeight * 6 + textLineHeight * 0.5 + "px",
                minHeight: textLineHeight + "px",
              }}
              onFocus={(e: any) => {
                if (!voteId || !session?.user?.email) {
                  e?.preventDefault();
                  e.target.blur();
                  setErrorToast("먼저 투표에 참여해주세요");
                }
              }}
            />
            <div className="flex gap-2">
              {mode === "edit" && (
                <Button
                  type="tertiary"
                  size="sm"
                  text={"취소"}
                  clickHandler={() => {
                    if (setMode) {
                      setMode("read");
                    }
                  }}
                />
              )}
              <Button
                btnClass={`${type}-${mode}-register-button`}
                type="primary"
                size="sm"
                text={"등록"}
                clickHandler={() => {
                  if (mode === "input" && opinionId) {
                    registerOpinion(
                      type,
                      session?.user?.email as string,
                      opinionId
                    );
                    return;
                  }
                  if (mode === "edit") {
                    editHandler();
                  }
                }}
              />
            </div>
          </div>
        </div>
        <CustomToast
          isSuccess={false}
          message={errorToast}
          isVisible={errorToast}
          onClose={() => setErrorToast(false)}
        />
        <CustomToast
          isSuccess={true}
          message={successToast}
          isVisible={successToast}
          onClose={() => setSuccessToast(false)}
          successIconFill={"#34BB7A"}
        />
      </div>
    );
  } else return null;
}

export default CommentInput;
